/* My Wallet — Empowerment Fund field screen.
 *
 * Feels like a game, not a form:
 *   - big balance ring (spent vs allowance)
 *   - fat "Log a Save 🎉" button
 *   - 3-field quick log (client autosuggest · amount · category chips)
 *   - trilingual preset story phrases
 *   - prohibited-categories reminder chip strip at log time
 *   - confetti on submit + toast celebration
 *   - countdown chip if a spend awaits a 24h log
 *
 * Mobile-first (390px), all colours pull from the app's cheerful field palette.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, PartyPopper, XCircle, Wallet as WalletIcon, Clock, GraduationCap, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ConfettiBurst from "../components/ConfettiBurst";

// UI copy — trilingual. Kept inline (page-specific).
const T = {
    title:         { en: "My Wallet",                        hi: "मेरा वॉलेट",                       bn: "আমার ওয়ালেট" },
    subtitle:      { en: "Delight clients on the spot.",     hi: "क्लाइंट्स को तुरंत खुश करें।",         bn: "গ্রাহকদের সঙ্গে সঙ্গে খুশি করুন।" },
    remaining:     { en: "Remaining",                        hi: "बाकी",                           bn: "বাকি" },
    of:            { en: "of",                               hi: "में से",                           bn: "মধ্যে" },
    log_a_save:    { en: "Log a Save 🎉",                    hi: "एक Save लॉग करें 🎉",             bn: "একটি Save লগ করুন 🎉" },
    festival_on:   { en: "Festival month — allowance doubled!", hi: "त्योहार माह — भत्ता दुगुना!",   bn: "উৎসবের মাস — ভাতা দ্বিগুণ!" },
    inactive:      { en: "Complete the 30-scenario training to activate your wallet.",
                     hi: "अपना वॉलेट सक्रिय करने के लिए 30-सिनेरियो प्रशिक्षण पूरा करें।",
                     bn: "আপনার ওয়ালেট চালু করতে ৩০টি সিনারিওর প্রশিক্ষণ শেষ করুন।" },
    open_training: { en: "Open training",                    hi: "प्रशिक्षण खोलें",                bn: "প্রশিক্ষণ খুলুন" },
    no_wallet:     { en: "You don't have a wallet at this site yet.", hi: "इस साइट पर आपका वॉलेट अभी नहीं है।", bn: "এই সাইটে আপনার এখনও ওয়ালেট নেই।" },
    client:        { en: "Client / tenant",                  hi: "क्लाइंट / किरायेदार",             bn: "গ্রাহক / ভাড়াটে" },
    amount_inr:    { en: "Amount (₹)",                       hi: "राशि (₹)",                       bn: "টাকা (₹)" },
    category:      { en: "Category",                         hi: "श्रेणी",                          bn: "শ্রেণি" },
    story:         { en: "One-line story (max 200)",         hi: "एक-लाइन कहानी (अधिकतम 200)",     bn: "এক লাইনের গল্প (সর্বোচ্চ ২০০)" },
    presets:       { en: "Preset phrases",                   hi: "तैयार वाक्य",                     bn: "প্রস্তুত বাক্য" },
    submit:        { en: "Submit",                           hi: "जमा करें",                        bn: "জমা দিন" },
    cancel:        { en: "Cancel",                           hi: "रद्द करें",                       bn: "বাতিল" },
    prohibited:    { en: "Never use the wallet for:",        hi: "इनके लिए वॉलेट का उपयोग कभी न करें:", bn: "এই কাজে ওয়ালেট কখনও নয়:" },
    my_entries:    { en: "My entries this month",            hi: "इस माह मेरी एंट्री",               bn: "এই মাসের আমার এন্ট্রি" },
    over_cap:      { en: "Over per-incident cap — raise an escalation first.", hi: "प्रति-घटना सीमा से अधिक — पहले एस्केलेशन उठाएँ।", bn: "প্রতি ঘটনার সীমা ছাড়িয়ে — আগে এস্কেলেশন তুলুন।" },
    log_pending:   { en: "Log a pending spend",              hi: "लंबित खर्च लॉग करें",             bn: "মুলতুবি খরচ লগ করুন" },
};

const CATEGORY_CHIPS = [
    { key: "recovery",         emoji: "🩹", en: "Recovery",       hi: "सेवा सुधार",     bn: "রিকভারি",       colour: "amber" },
    { key: "relationship",     emoji: "🤝", en: "Relationship",  hi: "रिश्ता",          bn: "সম্পর্ক",         colour: "emerald" },
    { key: "moment_of_truth",  emoji: "⚡", en: "Moment-of-truth", hi: "अहम पल",        bn: "গুরুত্বপূর্ণ মুহূর্ত", colour: "violet" },
];

const CHIP_STYLES = {
    amber:   "bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-800  data-[on=true]:bg-amber-500  data-[on=true]:text-white",
    emerald: "bg-emerald-50 hover:bg-emerald-100 border-emerald-300 text-emerald-800 data-[on=true]:bg-emerald-600 data-[on=true]:text-white",
    violet:  "bg-violet-50 hover:bg-violet-100 border-violet-300 text-violet-800 data-[on=true]:bg-violet-600 data-[on=true]:text-white",
};

function langOf(user) {
    return user?.language_preference || user?.language || "en";
}

function tr(user, key) {
    const lang = langOf(user);
    return T[key]?.[lang] || T[key]?.en || key;
}

export default function FundWalletPage() {
    const { user } = useAuth();
    const nav = useNavigate();
    const lang = langOf(user);

    const [walletState, setWalletState] = useState(null);
    const [entries, setEntries]     = useState([]);
    const [dict, setDict]           = useState(null);
    const [loading, setLoading]     = useState(true);

    // Log form state
    const [open, setOpen]           = useState(false);
    const [client, setClient]       = useState("");
    const [amount, setAmount]       = useState("");
    const [category, setCategory]   = useState("recovery");
    const [story, setStory]         = useState("");
    const [submitting, setSubmitting]= useState(false);
    const [confetti, setConfetti]   = useState(0);
    const [pendingSpends, setPendingSpends] = useState([]);   // countdown chips

    async function load() {
        setLoading(true);
        try {
            const [wRes, eRes, dRes] = await Promise.all([
                api.get("/fund/my-wallet"),
                api.get("/fund/entries"),
                api.get("/fund/dictionaries"),
            ]);
            setWalletState(wRes.data);
            setEntries(eRes.data || []);
            setDict(dRes.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const wallet = walletState?.wallet;
    const quizPassed = !!walletState?.quiz_passed;
    const festival = !!walletState?.festival_month;
    const cap = wallet?.per_incident_cap;
    const allowance = wallet?.monthly_allowance;
    const spent = wallet?.spent_to_date || 0;
    const remaining = Math.max(0, (allowance || 0) - spent);
    const pctSpent = allowance ? Math.min(100, Math.round((spent / allowance) * 100)) : 0;

    // Auto-suggest client names from user's recent entries
    const clientSuggestions = useMemo(() => {
        const set = new Set();
        entries.forEach((e) => set.add(e.client_or_tenant));
        return Array.from(set).slice(0, 10);
    }, [entries]);

    async function submitLog(e) {
        e?.preventDefault?.();
        if (!wallet) return;
        const amt = parseFloat(amount);
        if (!client.trim() || !amt || amt <= 0 || !story.trim()) {
            toast.error("Fill client, amount and story");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/fund/entries", {
                site_id: wallet.site_id,
                client_or_tenant: client.trim(),
                amount: amt,
                category,
                one_line_story: story.trim(),
            });
            setConfetti((c) => c + 1);
            toast.success("Logged! ⭐ FH will review.", { duration: 2400 });
            setClient(""); setAmount(""); setStory(""); setCategory("recovery");
            setOpen(false);
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail || apiError(err);
            toast.error(detail);
        } finally { setSubmitting(false); }
    }

    if (loading) return <div className="mx-auto max-w-3xl p-6" data-testid="fund-wallet-loading"><div className="h-64 rounded-2xl bg-white animate-pulse" /></div>;

    if (!wallet && quizPassed === false) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-6" data-testid="fund-wallet-quiz-gate">
                <div className="rounded-2xl bg-gradient-to-br from-violet-100 to-white border border-violet-200 p-6 text-center">
                    <GraduationCap className="mx-auto h-14 w-14 text-violet-600 mb-3" />
                    <div className="text-xl font-bold text-[#1E3355]">{tr(user, "title")}</div>
                    <div className="mt-2 text-gray-700">{tr(user, "inactive")}</div>
                    <button
                        onClick={() => nav("/fund/training")}
                        data-testid="fund-open-training-btn"
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1E3355] hover:bg-[#152541] text-white font-semibold px-5 py-2.5"
                    >
                        <Sparkles className="w-4 h-4" /> {tr(user, "open_training")}
                    </button>
                </div>
            </div>
        );
    }

    if (!wallet) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-6" data-testid="fund-wallet-none">
                <div className="rounded-2xl bg-white border border-gray-100 p-6 text-center text-gray-700">{tr(user, "no_wallet")}</div>
            </div>
        );
    }

    // Ring geometry
    const size = 200;
    const stroke = 18;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const dashOffset = c - (c * pctSpent) / 100;

    return (
        <div className="mx-auto max-w-3xl px-4 py-6" data-testid="fund-wallet-page">
            {confetti > 0 && <ConfettiBurst key={confetti} />}

            {/* Header */}
            <div className="flex items-baseline justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-[#1E3355]">{tr(user, "title")}</h1>
                    <div className="text-sm text-gray-500">{tr(user, "subtitle")}</div>
                </div>
            </div>

            {festival && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-red-100 text-red-800 px-3 py-1 text-xs font-semibold" data-testid="fund-festival-banner">
                    🎊 {tr(user, "festival_on")}
                </div>
            )}

            {pendingSpends.length > 0 && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-semibold" data-testid="fund-pending-chip">
                    <Clock className="w-3.5 h-3.5" /> {pendingSpends.length} {tr(user, "log_pending")}
                </div>
            )}

            {/* Balance ring */}
            <div className="mt-4 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-amber-50 border border-emerald-100 shadow-sm p-5 flex flex-col items-center" data-testid="fund-wallet-ring">
                <div className="relative">
                    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                        <circle cx={size/2} cy={size/2} r={r} stroke="#E5E7EB" strokeWidth={stroke} fill="none" />
                        <circle
                            cx={size/2} cy={size/2} r={r}
                            stroke={pctSpent >= 100 ? "#E02B2B" : pctSpent >= 80 ? "#F59E0B" : "#10B981"}
                            strokeWidth={stroke} fill="none"
                            strokeLinecap="round"
                            strokeDasharray={c}
                            strokeDashoffset={dashOffset}
                            style={{ transform: `rotate(-90deg)`, transformOrigin: "center", transition: "stroke-dashoffset 700ms cubic-bezier(0.4, 0, 0.2, 1)" }}
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="text-xs uppercase tracking-widest text-gray-500">{tr(user, "remaining")}</div>
                        <div className="text-4xl font-bold text-[#1E3355]" data-testid="fund-wallet-remaining">₹{remaining.toLocaleString("en-IN")}</div>
                        <div className="text-[11px] text-gray-500">{tr(user, "of")} ₹{Number(allowance).toLocaleString("en-IN")}</div>
                    </div>
                </div>

                <div className="mt-2 text-[11px] text-gray-500">
                    Per-incident cap: <span className="font-semibold text-[#1E3355]">₹{Number(cap).toLocaleString("en-IN")}</span>
                </div>

                <button
                    onClick={() => setOpen(true)}
                    disabled={wallet.status !== "active"}
                    data-testid="fund-log-a-save-btn"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#E02B2B] hover:bg-[#c72525] disabled:bg-gray-300 text-white font-bold px-6 py-3 text-base shadow-lg shadow-red-200 active:scale-95 transition-transform"
                >
                    <PartyPopper className="w-5 h-5" /> {tr(user, "log_a_save")}
                </button>
            </div>

            {/* Log form modal */}
            {open && (
                <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-3" onClick={() => setOpen(false)} data-testid="fund-log-modal">
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={submitLog}
                        className="w-full sm:max-w-lg rounded-3xl sm:rounded-2xl bg-white p-5 shadow-2xl"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-lg font-bold text-[#1E3355] inline-flex items-center gap-2"><PartyPopper className="w-5 h-5 text-red-600" /> {tr(user, "log_a_save")}</div>
                            <button type="button" onClick={() => setOpen(false)} data-testid="fund-log-cancel"><XCircle className="w-6 h-6 text-gray-400" /></button>
                        </div>

                        {/* Prohibited categories chip strip (reminder) */}
                        {dict?.prohibited?.length > 0 && (
                            <div className="mb-3 rounded-xl bg-red-50 border border-red-100 p-2.5" data-testid="fund-prohibited-strip">
                                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-red-700 font-semibold mb-1"><ShieldAlert className="w-3.5 h-3.5" /> {tr(user, "prohibited")}</div>
                                <div className="flex flex-wrap gap-1">
                                    {dict.prohibited.map((p) => (
                                        <span key={p.key} className="inline-flex items-center gap-1 rounded-full bg-white border border-red-200 px-2 py-0.5 text-[10px] text-red-700">✕ {p.label_en}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <label className="block text-xs font-semibold text-gray-600 mb-1">{tr(user, "client")}</label>
                        <input
                            list="fund-client-suggestions"
                            value={client}
                            onChange={(e) => setClient(e.target.value)}
                            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E3355]"
                            data-testid="fund-log-client"
                            required
                        />
                        <datalist id="fund-client-suggestions">
                            {clientSuggestions.map((s) => <option key={s} value={s} />)}
                        </datalist>

                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">{tr(user, "amount_inr")}</label>
                                <input
                                    type="number" min="1" step="1"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E3355]"
                                    data-testid="fund-log-amount"
                                    required
                                />
                                {amount && parseFloat(amount) > cap && (
                                    <div className="mt-1 text-[10px] text-red-700 leading-snug" data-testid="fund-log-cap-warn">{tr(user, "over_cap")}</div>
                                )}
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">{tr(user, "category")}</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {CATEGORY_CHIPS.map((c) => (
                                        <button
                                            key={c.key}
                                            type="button"
                                            data-on={category === c.key}
                                            data-testid={`fund-log-cat-${c.key}`}
                                            onClick={() => setCategory(c.key)}
                                            className={`text-xs rounded-full border px-2.5 py-1 font-semibold transition ${CHIP_STYLES[c.colour]}`}
                                        >
                                            {c.emoji} {c[lang] || c.en}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <label className="block text-xs font-semibold text-gray-600 mb-1 mt-3">{tr(user, "story")}</label>
                        <textarea
                            value={story} maxLength={200}
                            onChange={(e) => setStory(e.target.value)}
                            rows={2}
                            className="w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1E3355] resize-none"
                            data-testid="fund-log-story"
                            required
                        />
                        <div className="text-[10px] text-gray-400 text-right">{story.length} / 200</div>

                        {dict?.preset_phrases?.length > 0 && (
                            <>
                                <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-2 mb-1">{tr(user, "presets")}</div>
                                <div className="flex flex-col gap-1.5" data-testid="fund-log-presets">
                                    {dict.preset_phrases.map((p, i) => (
                                        <button
                                            key={i} type="button"
                                            data-testid={`fund-log-preset-${i}`}
                                            onClick={() => setStory(p[lang] || p.en)}
                                            className="text-left text-xs rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 px-2.5 py-1.5 italic text-gray-700"
                                        >
                                            "{p[lang] || p.en}"
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        <div className="mt-4 flex gap-2 justify-end">
                            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-700" data-testid="fund-log-cancel-2">{tr(user, "cancel")}</button>
                            <button type="submit" disabled={submitting} className="rounded-full bg-[#1E3355] hover:bg-[#152541] disabled:bg-gray-300 text-white font-semibold px-5 py-2 text-sm" data-testid="fund-log-submit">
                                {submitting ? "…" : tr(user, "submit")}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* My entries this month */}
            <div className="mt-6" data-testid="fund-my-entries">
                <h2 className="text-sm uppercase tracking-widest text-gray-500 font-bold inline-flex items-center gap-1.5 mb-2"><WalletIcon className="w-4 h-4" /> {tr(user, "my_entries")}</h2>
                {entries.length === 0 && <div className="rounded-xl bg-white border border-gray-100 p-4 text-gray-400 italic">No entries yet.</div>}
                <div className="space-y-2">
                    {entries.map((e) => (
                        <div key={e.id} className="rounded-xl bg-white border border-gray-100 p-3" data-testid={`fund-entry-row-${e.id}`}>
                            <div className="flex items-baseline justify-between">
                                <div className="font-medium text-[#1E3355]">{e.client_or_tenant}</div>
                                <div className="text-sm font-bold text-emerald-700">₹{Number(e.amount).toLocaleString("en-IN")}</div>
                            </div>
                            <div className="text-xs italic text-gray-700 mt-0.5">"{e.one_line_story}"</div>
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[10px]">
                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
                                    e.status === "verified"  ? "bg-emerald-100 text-emerald-800" :
                                    e.status === "flagged"   ? "bg-red-100 text-red-800" :
                                    "bg-gray-100 text-gray-700"
                                }`}>{e.status.toUpperCase()}</span>
                                <span className="text-gray-400">· {e.day}</span>
                                {e.category && <span className="text-gray-400">· {e.category}</span>}
                                {e.flag_reason && <span className="ml-1 text-red-700 italic">— {e.flag_reason}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
